import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AdminApi } from "../../api/services";
import { errorMessage } from "../../api/client";
import { EmptyState, PageHeader, SimBadge, Spinner, StatusBadge } from "../../components/common";
import { dateTime, phone } from "../../lib/format";

export default function AdminVerification() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin", "users", "responder"], queryFn: () => AdminApi.users({ role: "responder" }) });
  const verify = useMutation({
    mutationFn: ({ id, decision, notes }: any) => AdminApi.verify(id, decision, notes),
    onSuccess: () => { toast.success("Verification updated"); qc.invalidateQueries({ queryKey: ["admin"] }); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (isLoading) return <Spinner />;
  const uploadPhoto = (id: string, file: File) => {
    const img = new Image();
    img.onload = async () => {
      const s = 256, cv = document.createElement("canvas");
      const k = Math.min(1, s / Math.max(img.width, img.height));
      cv.width = img.width * k; cv.height = img.height * k;
      cv.getContext("2d").drawImage(img, 0, 0, cv.width, cv.height);
      try { await AdminApi.setPhoto(id, cv.toDataURL("image/jpeg", 0.85)); toast.success("Driver photo saved"); qc.invalidateQueries({ queryKey: ["admin"] }); }
      catch (e) { toast.error(errorMessage(e)); }
    };
    img.src = URL.createObjectURL(file);
  };
  const list = [...(data || [])].sort((a, b) => Number(a.verification?.status === "verified") - Number(b.verification?.status === "verified"));
  return (
    <div className="space-y-4">
      <PageHeader title="Responder verification" subtitle="Approve identity and credentials before a responder can go on duty." />
      {!list.length && <EmptyState title="No responders yet" />}
      {list.map((r) => {
        const v = r.verification || {};
        return (
          <div key={r.id} className="fp-card flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid="verification-item">
            <div className="space-y-1 text-sm">
              <p className="flex flex-wrap items-center gap-2 font-semibold">{r.name}<SimBadge show={r.is_simulated} /><StatusBadge status={v.status || "pending"} testId={`verification-status-${r.id}`} /></p>
              <p className="text-slate-600">{r.email} · {phone(r.phone)}</p>
              <p className="text-slate-600">License: {v.license_number || "—"} · ID: {v.id_type || "—"} {v.id_number || ""}</p>
              {v.reviewed_by && <p className="text-xs text-slate-500">Reviewed by {v.reviewed_by} {v.reviewed_at ? `· ${dateTime(v.reviewed_at)}` : ""}{v.notes ? ` · ${v.notes}` : ""}</p>}
            </div>
            <div className="flex gap-2">
              <label className="fp-btn-secondary cursor-pointer" data-testid={`photo-upload-${r.id}`}>
                {r.photo ? <img src={r.photo} alt="" className="h-6 w-6 rounded-full object-cover" /> : null} Photo
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(r.id, e.target.files[0])} />
              </label>
              {v.status !== "verified" && <button className="fp-btn-primary" onClick={() => verify.mutate({ id: r.id, decision: "verified", notes: "" })} data-testid={`verify-approve-${r.id}`}>Approve</button>}
              {v.status !== "rejected" && <button className="fp-btn-secondary text-amber-800" onClick={() => { const n = window.prompt("Reason for rejection"); if (n !== null) verify.mutate({ id: r.id, decision: "rejected", notes: n }); }} data-testid={`verify-reject-${r.id}`}>{v.status === "verified" ? "Revoke" : "Reject"}</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
